import { useState, useEffect, useCallback, useRef } from 'react';
import { ContactStore, Contact } from './stores/ContactStore';
import { ContactSyncManager, SyncProgress } from './stores/ContactSyncManager';
import { SearchIndexManager, SearchResult } from './stores/SearchIndexManager';

interface ContactsState {
  contacts: Contact[];
  searchResults: SearchResult[];
  isLoading: boolean;
  isSyncing: boolean;
  error: string | null;
  syncProgress: SyncProgress | null;
  syncStatus: {
    isBootstrapComplete: boolean;
    lastFullSync: string | null;
    lastIncrementalSync: string | null;
    totalContacts: number;
    personalBootstrapComplete: boolean;
    personalLastFullSync: string | null;
    personalLastIncrementalSync: string | null;
    personalTotalContacts: number;
    internalBootstrapComplete: boolean;
    internalLastFullSync: string | null;
    internalLastIncrementalSync: string | null;
    internalTotalUsers: number;
  } | null;
}

export const useContacts = () => {
  const [contactsState, setContactsState] = useState<ContactsState>({
    contacts: [],
    searchResults: [],
    isLoading: false,
    isSyncing: false,
    error: null,
    syncProgress: null,
    syncStatus: null,
  });

  const storeRef = useRef<ContactStore | null>(null);
  const syncManagerRef = useRef<ContactSyncManager | null>(null);
  const searchManagerRef = useRef<SearchIndexManager | null>(null);

  // Initialize stores
  useEffect(() => {
    const store = new ContactStore();
    const syncManager = new ContactSyncManager(true); // Outlook context - filter contacts without emails
    const searchManager = new SearchIndexManager(store, true); // Outlook context - filter search results
    
    storeRef.current = store;
    syncManagerRef.current = syncManager;
    searchManagerRef.current = searchManager;

    // Load initial contacts from local store
    loadLocalContacts();
    loadSyncStatus();

    return () => {
      // Cleanup: destroy sync manager and normalizer
      syncManager.destroy();
    };
  }, []);

  const loadLocalContacts = async () => {
    if (!storeRef.current) return;

    try {
      setContactsState(prev => ({ ...prev, isLoading: true }));
      const contacts = await storeRef.current.getAllContacts();
      setContactsState(prev => ({ 
        ...prev, 
        contacts,
        isLoading: false 
      }));
    } catch (error) {
      console.error('Error loading local contacts:', error);
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Failed to load local contacts',
        isLoading: false 
      }));
    }
  };

  const loadSyncStatus = async () => {
    if (!syncManagerRef.current || !storeRef.current) return;

    try {
      const status = await syncManagerRef.current.getSyncStatus();
      const meta = await storeRef.current.getMeta();
      
      setContactsState(prev => ({ 
        ...prev, 
        syncStatus: {
          ...status,
          personalBootstrapComplete: meta.personalBootstrapComplete,
          personalLastFullSync: meta.personalLastFullSync,
          personalLastIncrementalSync: meta.personalLastIncrementalSync,
          personalTotalContacts: meta.personalTotalContacts,
          internalBootstrapComplete: meta.internalBootstrapComplete,
          internalLastFullSync: meta.internalLastFullSync,
          internalLastIncrementalSync: meta.internalLastIncrementalSync,
          internalTotalUsers: meta.internalTotalUsers,
        }
      }));
    } catch (error) {
      console.error('Error loading sync status:', error);
    }
  };

  const handleSyncProgress = useCallback((progress: SyncProgress) => {
    setContactsState(prev => ({ 
      ...prev, 
      syncProgress: progress,
      isSyncing: true 
    }));
  }, []);

  const handleSyncError = useCallback((error: Error) => {
    setContactsState(prev => ({ 
      ...prev, 
      error: error.message,
      isSyncing: false,
      syncProgress: null 
    }));
  }, []);

  const fetchContactsFromSharedMailbox = async (accessToken: string, getTokenCallback?: () => Promise<string>) => {
    if (!syncManagerRef.current) {
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    // Prevent multiple simultaneous syncs
    if (contactsState.isSyncing || contactsState.isLoading) {
      console.log('Sync already in progress, skipping...');
      return;
    }

    setContactsState(prev => ({ ...prev, isLoading: true, isSyncing: true, error: null }));

    try {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        throw new Error('SHARED_UPN environment variable is required');
      }

      const syncManager = syncManagerRef.current;
      const status = await syncManager.getSyncStatus();

      // Debug current metadata state
      await syncManager.debugMetadata();

      if (!status.isBootstrapComplete) {
        // First time: bootstrap sync
        console.log('Starting bootstrap sync...');
        await syncManager.bootstrapSync(accessToken, sharedUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else if (!status.hasDeltaToken) {
        // Bootstrap complete but no delta token - fall back to bootstrap
        console.warn('Bootstrap complete but no delta token found, falling back to bootstrap sync...');
        await syncManager.bootstrapSync(accessToken, sharedUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else {
        // Subsequent times: incremental sync
        console.log('Starting incremental sync...');
        await syncManager.incrementalSync(accessToken, sharedUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      }

      // Start auto sync with token refresh callback and UI update callback with a delay
      console.log('About to start auto-sync with:', {
        hasAccessToken: !!accessToken,
        sharedUpn,
        hasTokenCallback: !!getTokenCallback
      });
      setTimeout(() => {
        syncManager.startAutoSync(accessToken, sharedUpn, getTokenCallback);
        console.log('Auto-sync start call completed');
      }, 1000); // 1 second delay

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      setContactsState(prev => ({ 
        ...prev, 
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));

    } catch (err) {
      console.error('Error syncing contacts:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to sync contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  };

  const fetchPersonalContacts = async (accessToken: string, userUpn: string, getTokenCallback?: () => Promise<string>) => {
    console.log('fetchPersonalContacts called with:', { userUpn, hasAccessToken: !!accessToken });
    
    if (!syncManagerRef.current) {
      console.error('Sync manager not initialized');
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    // Prevent multiple simultaneous syncs
    if (contactsState.isSyncing || contactsState.isLoading) {
      console.log('Sync already in progress, skipping personal contacts sync...');
      return;
    }

    console.log('Starting personal contacts sync...');
    setContactsState(prev => ({ ...prev, isLoading: true, isSyncing: true, error: null }));

    try {
      const syncManager = syncManagerRef.current;
      const meta = await storeRef.current?.getMeta();
      
      console.log('Personal contacts metadata:', {
        personalBootstrapComplete: meta?.personalBootstrapComplete,
        personalDeltaToken: meta?.personalDeltaToken ? 'exists' : 'missing',
        personalTotalContacts: meta?.personalTotalContacts
      });

      if (!meta?.personalBootstrapComplete) {
        // First time: bootstrap sync for personal contacts
        console.log('Starting personal contacts bootstrap sync...');
        await syncManager.bootstrapPersonalSync(accessToken, userUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else if (!meta?.personalDeltaToken) {
        // Bootstrap complete but no delta token - fall back to bootstrap
        console.warn('Personal bootstrap complete but no delta token found, falling back to bootstrap sync...');
        await syncManager.bootstrapPersonalSync(accessToken, userUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else {
        // Subsequent times: incremental sync for personal contacts
        console.log('Starting personal contacts incremental sync...');
        await syncManager.incrementalPersonalSync(accessToken, userUpn, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      }

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      setContactsState(prev => ({ 
        ...prev, 
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));

    } catch (err) {
      console.error('Error syncing personal contacts:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to sync personal contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  };

  const searchContacts = async (query: string): Promise<SearchResult[]> => {
    if (!searchManagerRef.current || !query.trim()) {
      setContactsState(prev => ({ ...prev, searchResults: [] }));
      return [];
    }

    try {
      const results = await searchManagerRef.current.search(query);
      setContactsState(prev => ({ ...prev, searchResults: results }));
      return results;
    } catch (error) {
      console.error('Error searching contacts:', error);
      setContactsState(prev => ({ ...prev, searchResults: [] }));
      return [];
    }
  };

  const clearSearch = () => {
    setContactsState(prev => ({ ...prev, searchResults: [] }));
  };


  const forceResync = async (accessToken: string) => {
    if (!syncManagerRef.current) {
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    setContactsState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        throw new Error('SHARED_UPN environment variable is required');
      }

      const syncManager = syncManagerRef.current;
      await syncManager.forceResync(accessToken, sharedUpn, {
        onProgress: handleSyncProgress,
        onError: handleSyncError,
      });

      // Start auto sync with a delay
      setTimeout(() => {
        syncManager.startAutoSync(accessToken, sharedUpn);
      }, 1000); // 1 second delay

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      setContactsState(prev => ({ 
        ...prev, 
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));

    } catch (err) {
      console.error('Error force resyncing contacts:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to resync contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  };

  const parallelStartupSync = useCallback(async (accessToken: string, userUpn: string) => {
    if (!syncManagerRef.current) {
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    // Don't set isLoading for fast startup - it should be instant and silent
    // setContactsState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        throw new Error('SHARED_UPN environment variable is required');
      }

      const syncManager = syncManagerRef.current;
      
      console.log('Starting fast startup sync for all contact sources...');
      await syncManager.fastStartupSync(accessToken, sharedUpn, userUpn);

      // Background delta sync and auto-sync timers are now handled automatically by fastStartupSync
      // No need to manually start auto-sync timers

      // Don't reload contacts and status during fast startup to avoid loading UI
      // The initial loadLocalContacts() and loadSyncStatus() in useEffect is sufficient
      // await loadLocalContacts();
      // await loadSyncStatus();

      // Don't set isLoading: false since we never set it to true for fast startup
      // setContactsState(prev => ({ 
      //   ...prev, 
      //   isLoading: false,
      //   isSyncing: false,
      //   syncProgress: null 
      // }));

      console.log('Fast startup sync completed successfully');

    } catch (err) {
      console.error('Error during fast startup sync:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to startup sync contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  }, [handleSyncProgress, handleSyncError, loadLocalContacts, loadSyncStatus]);

  const forceParallelResync = useCallback(async (accessToken: string, userUpn: string) => {
    if (!syncManagerRef.current) {
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    setContactsState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        throw new Error('SHARED_UPN environment variable is required');
      }

      const syncManager = syncManagerRef.current;
      
      console.log('Starting parallel resync for all contact sources...');
      await syncManager.smartParallelSync(accessToken, sharedUpn, userUpn, {
        onProgress: handleSyncProgress,
        onError: handleSyncError,
        forceBootstrap: true, // Force bootstrap sync for clean resync
      });

      // Background delta sync is now handled automatically by smartParallelSync
      // No need to manually start auto-sync timers

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      setContactsState(prev => ({ 
        ...prev, 
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));

      console.log('Parallel resync completed successfully');

    } catch (err) {
      console.error('Error force resyncing contacts:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to resync contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  }, [handleSyncProgress, handleSyncError, loadLocalContacts, loadSyncStatus]);

  const forcePersonalResync = async (accessToken: string, userUpn: string) => {
    if (!syncManagerRef.current) {
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    setContactsState(prev => ({ ...prev, isLoading: true, error: null }));

    try {
      const syncManager = syncManagerRef.current;
      await syncManager.forcePersonalResync(accessToken, userUpn, {
        onProgress: handleSyncProgress,
        onError: handleSyncError,
      });

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      setContactsState(prev => ({ 
        ...prev, 
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));

    } catch (err) {
      console.error('Error force resyncing personal contacts:', err);
      setContactsState(prev => ({ 
        ...prev, 
        error: `Failed to resync personal contacts: ${err instanceof Error ? err.message : 'Unknown error'}`,
        isLoading: false,
        isSyncing: false,
        syncProgress: null 
      }));
    }
  };

  const clearContacts = async () => {
    if (!storeRef.current) return;

    try {
      await storeRef.current.clearAllData();
      setContactsState(prev => ({ 
        ...prev, 
        contacts: [],
        searchResults: [],
        syncStatus: null 
      }));
    } catch (error) {
      console.error('Error clearing contacts:', error);
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Failed to clear contacts' 
      }));
    }
  };

  const getContactById = async (id: string): Promise<Contact | null> => {
    if (!storeRef.current) return null;
    return await storeRef.current.getContact(id);
  };

  const getContactsBySourceType = async (sourceType: 'corporate' | 'personal' | 'internal'): Promise<Contact[]> => {
    if (!storeRef.current) return [];
    const contacts = await storeRef.current.getContactsBySourceType(sourceType);
    return contacts;
  };

  const resetSyncState = () => {
    if (syncManagerRef.current) {
      syncManagerRef.current.resetSyncState();
    }
    setContactsState(prev => ({ 
      ...prev, 
      isSyncing: false,
      isLoading: false,
      error: null 
    }));
  };

  const checkAutoSyncStatus = () => {
    if (syncManagerRef.current) {
      syncManagerRef.current.checkAutoSyncStatus();
    }
  };

  const manualStartAutoSync = async (accessToken: string, getTokenCallback?: () => Promise<string>) => {
    if (syncManagerRef.current) {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        console.error('SHARED_UPN environment variable is required');
        return;
      }
      syncManagerRef.current.manualStartAutoSync(accessToken, sharedUpn, getTokenCallback);
    }
  };

  const refreshUI = async () => {
    console.log('Manually refreshing UI...');
    await loadLocalContacts();
    await loadSyncStatus();
  };

  const checkAndPerformStartupSync = async (accessToken: string, getTokenCallback?: () => Promise<string>) => {
    if (syncManagerRef.current) {
      const sharedUpn = process.env.SHARED_UPN;
      if (!sharedUpn) {
        console.error('SHARED_UPN environment variable is required');
        return false;
      }
      
      return await syncManagerRef.current.checkAndPerformStartupSync(
        accessToken, 
        sharedUpn, 
        getTokenCallback
      );
    }
    return false;
  };

  const checkAndPerformPersonalStartupSync = async (accessToken: string, userUpn: string, getTokenCallback?: () => Promise<string>) => {
    if (syncManagerRef.current) {
      return await syncManagerRef.current.checkAndPerformPersonalStartupSync(
        accessToken, 
        userUpn, 
        getTokenCallback
      );
    }
    return false;
  };

  const checkAndPerformInternalStartupSync = async (accessToken: string, getTokenCallback?: () => Promise<string>) => {
    if (syncManagerRef.current) {
      return await syncManagerRef.current.checkAndPerformInternalStartupSync(
        accessToken, 
        getTokenCallback
      );
    }
    return false;
  };

  const fetchInternalUsers = async (accessToken: string, getTokenCallback?: () => Promise<string>) => {
    console.log('fetchInternalUsers called with:', { hasAccessToken: !!accessToken });
    
    if (!syncManagerRef.current) {
      console.error('Sync manager not initialized');
      setContactsState(prev => ({ 
        ...prev, 
        error: 'Sync manager not initialized' 
      }));
      return;
    }

    // Don't check sync state for internal users - allow it to run independently
    console.log('Starting internal users sync...');
    // Don't set global sync state for internal users sync

    try {
      const syncManager = syncManagerRef.current;
      const meta = await storeRef.current?.getMeta();
      
      console.log('Internal users metadata:', {
        internalBootstrapComplete: meta?.internalBootstrapComplete,
        internalDeltaToken: meta?.internalDeltaToken ? 'exists' : 'missing',
        internalTotalUsers: meta?.internalTotalUsers
      });

      if (!meta?.internalBootstrapComplete) {
        // First time: bootstrap sync for internal users
        console.log('Starting internal users bootstrap sync...');
        await syncManager.bootstrapInternalSync(accessToken, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else if (!meta?.internalDeltaToken) {
        // Bootstrap complete but no delta token - fall back to bootstrap
        console.warn('Internal bootstrap complete but no delta token found, falling back to bootstrap sync...');
        await syncManager.bootstrapInternalSync(accessToken, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      } else {
        // Subsequent times: incremental sync for internal users
        console.log('Starting internal users incremental sync...');
        await syncManager.incrementalInternalSync(accessToken, {
          onProgress: handleSyncProgress,
          onError: handleSyncError,
        });
      }

      // Start auto sync for internal users with a delay
      console.log('About to start internal auto-sync with:', {
        hasAccessToken: !!accessToken,
        hasTokenCallback: !!getTokenCallback
      });
      setTimeout(() => {
        syncManager.startInternalAutoSync(accessToken, getTokenCallback);
        console.log('Internal auto-sync start call completed');
      }, 1000); // 1 second delay

      // Reload contacts and status
      await loadLocalContacts();
      await loadSyncStatus();

      console.log('Internal users sync completed successfully');

    } catch (err) {
      console.error('Error syncing internal users:', err);
      // Don't set global error state for internal users sync
    }
  };

  return {
    ...contactsState,
    contactStore: storeRef.current,
    fetchContactsFromSharedMailbox,
    fetchPersonalContacts,
    fetchInternalUsers,
    searchContacts,
    clearSearch,
    forceResync,
    forceParallelResync,
    parallelStartupSync,
    forcePersonalResync,
    clearContacts,
    getContactById,
    getContactsBySourceType,
    loadLocalContacts,
    resetSyncState,
    checkAutoSyncStatus,
    manualStartAutoSync,
    refreshUI,
    checkAndPerformStartupSync,
    checkAndPerformPersonalStartupSync,
    checkAndPerformInternalStartupSync,
  };
};

